// Trade Offers — types matching the as-built backend contract (offer-model ticket,
// build report 2026-07-28 + corrections). Statuses are lowercase on the wire; totals
// are role-shaped (proposerSends/recipientSends) and each surface computes they/you
// from the viewer — there is deliberately no signed net anywhere in the contract.

export type TradeOfferStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'failed';

export type TradeOfferItemDirection = 'PROPOSER_SENDS' | 'RECIPIENT_SENDS';

export interface OfferParticipantStats {
  rating: number | null;
  ratingCount: number;
  cardsSent: number;
}

export interface OfferParticipant {
  id: number;
  username: string;
  isElite: boolean;
  /**
   * Avatar block, always present and mirroring the public-profile serializer
   * field for field (avatars ticket, backend 2026-07-29). showAnimatedBorder is
   * the user's STORED setting — the client renders the Elite border only when
   * isElite && showAnimatedBorder, exactly as the profile does.
   */
  avatarUrl: string | null;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;
  stats: OfferParticipantStats;
}

export interface OfferCard {
  id: number;
  oracleId: string | null;
  scryfallId: string | null;
  name: string;
  setCode: string;
  setName: string;
  collectorNumber: string;
  lang: string;
  imageUrl: string | null;
}

export interface TradeOfferItem {
  id: number;
  direction: TradeOfferItemDirection;
  // Null when the referenced have row was deleted after the offer was created
  userMTGCardHaveId: number | null;
  quantity: number;
  foil: boolean;
  condition: string;
  /** Per-unit price locked at offer creation */
  lockedPricePoints: number;
  lineTotalPoints: number;
  card: OfferCard;
}

export interface TradeOfferTotals {
  proposerSendsPoints: number;
  recipientSendsPoints: number;
}

export interface TradeOffer {
  id: string;
  status: TradeOfferStatus;
  proposer: OfferParticipant;
  recipient: OfferParticipant;
  createdAt: string;
  expiresAt: string;
  respondedAt: string | null;
  failureReason: string | null;
  /** Populated once the offer is accepted */
  trades: { proposerTradeId: string; recipientTradeId: string } | null;
  totals: TradeOfferTotals;
  items: TradeOfferItem[];
}

export interface CreateTradeOfferItemInput {
  userMTGCardHaveId: number;
  quantity: number;
  /**
   * Proposer-side items only: the declared condition, sent ONLY when it
   * differs from the have row's condition (omit-when-equal keeps the
   * untouched-selector payload byte-identical to the pre-selector client).
   * The server validates it against the recipient's want record and computes
   * lockedPricePoints from it; recipient items never carry one.
   */
  condition?: string;
}

export interface CreateTradeOfferRequest {
  recipientId: number;
  proposerItems: CreateTradeOfferItemInput[];
  recipientItems: CreateTradeOfferItemInput[];
  /**
   * Present only when the builder was seeded from a trade suggestion; passed
   * through unchanged so the backend can release the suggestion's allocation.
   * Never synthesized for any other entry path.
   */
  suggestionId?: string;
}

export interface ListTradeOffersRequest {
  direction: 'incoming' | 'outgoing';
  /** Comma-separated statuses; the backend accepts either case */
  status?: string;
  page?: number;
  limit?: number;
}

/** Per-status offer counts for one direction; every key always present, explicit zeros */
export interface OfferStatusCounts {
  pending: number;
  accepted: number;
  declined: number;
  expired: number;
  cancelled: number;
  failed: number;
}

export interface ListTradeOffersResponse {
  offers: TradeOffer[];
  /** Total for the CURRENT query — status-filtered when a status param is set */
  totalCount: number;
  page: number;
  hasMore: boolean;
  /**
   * Counts for the requested DIRECTION, deliberately IGNORING the status
   * filter — they drive filter-pill visibility, which must survive an active
   * filter. Asymmetric with totalCount by design: with a status filter set,
   * totalCount is filtered while these are not, so they will not agree.
   * Optional only as rollout tolerance for an older backend.
   */
  statusCounts?: OfferStatusCounts;
}

export interface AcceptTradeOfferResponse {
  success: boolean;
  trades: { proposerTradeId: string; recipientTradeId: string };
  openedSimultaneously: boolean;
}

export interface CommittedQuantityEntry {
  committedQuantity: number;
  offerIds: string[];
}

/**
 * PENDING offers only. byCardKey keys are `${cardId}|${CONDITION}|${lang}|foil`
 * or `...|nonfoil` — the attribute-shaped join key for the existing send flow,
 * whose rows do not carry have-row ids.
 */
export interface MyCommittedQuantitiesResponse {
  byHaveId: Record<string, CommittedQuantityEntry>;
  byCardKey: Record<string, CommittedQuantityEntry>;
}

export interface OfferCandidateRow {
  userMTGCardHaveId: number;
  ownedQuantity: number;
  foil: boolean;
  condition: string;
  /**
   * Computed by the same backend helper that locks prices at offer creation.
   * Null means no price data exists: the add must be blocked inline — never
   * approximate, never fall back to another price field.
   */
  quotedPricePoints: number | null;
  /**
   * side=send only: quoted price per DECLARABLE condition (all four; the
   * backend computes each through the same helper that locks prices). null
   * value = no price data for that condition. Intersect with the want entry's
   * acceptedConditions client-side.
   */
  quotedPricesByCondition?: Record<string, number | null>;
  card: OfferCard;
  /** side=receive only: ids of the viewer's want entries this row matches */
  matchedWantIds?: number[];
}

export interface OfferCandidateWantGroup {
  wantEntry: {
    id: number;
    cardName: string;
    quantity: number;
    oracleId: string | null;
    /**
     * The recipient's EXPLICIT effective accepted-condition list, derived by
     * the shared backend matcher (acceptableConditions + minCondition). An
     * accepts-any want lists all four — never an empty-means-any sentinel.
     */
    acceptedConditions?: string[];
  };
  /** Empty in full mode when the viewer owns no matching row (view-only row) */
  matches: OfferCandidateRow[];
}

export interface OfferCandidatesRequest {
  userId: number;
  side: 'send' | 'receive';
  mode?: 'intersection' | 'full';
  page?: number;
  limit?: number;
  search?: string;
  /**
   * Highest value first, applied server-side BEFORE pagination — the ordering
   * has to span pages, which is why it is not a client-side sort.
   *
   * The backend orders on the indexed `referencePrice` while the row displays
   * `quotedPricePoints`; pricing a whole matched set to sort by the quote costs
   * minutes per page load on large collections. The two agree on ~99.7% of
   * pairwise comparisons, so a small number of adjacent rows can sit one
   * position off relative to the points shown. That is expected, not a bug.
   *
   * Omitting this preserves the endpoint's prior ordering exactly.
   */
  sort?: 'value_desc';
}

export interface OfferCandidatesSendResponse {
  candidates: OfferCandidateWantGroup[];
  totalCount: number;
  page: number;
  hasMore: boolean;
}

export interface OfferCandidatesReceiveResponse {
  candidates: OfferCandidateRow[];
  totalCount: number;
  page: number;
  hasMore: boolean;
}

/** One line item in the builder columns; binds a specific have row, never a card name */
export interface OfferLineItem {
  userMTGCardHaveId: number;
  quantity: number;
  ownedQuantity: number;
  /** Price of the DECLARED condition; rows with null price are never addable */
  unitPricePoints: number;
  foil: boolean;
  /** The have row's condition (identity — never changed by declaration) */
  condition: string;
  /**
   * youSend only: the condition the sender declares they will ship. Defaults
   * to the row's condition; omitted from the POST body when equal to it.
   */
  declaredCondition?: string;
  /** youSend candidates only: powers the row's condition selector after add */
  quotedPricesByCondition?: Record<string, number | null>;
  /** The row condition's own quote (the map covers only declarable conditions) */
  rowQuotedPricePoints?: number | null;
  /** youSend candidates only: the recipient's accepted set for this want */
  acceptedConditions?: string[];
  /**
   * The want entry this line was created from — set on EVERY send-side line as
   * of storage schema v5, not just un-catalogued adds. Two jobs:
   *
   *   1. The full-want-list row derives its "Added" state from the presence of
   *      this line, so that state lives with the line and dies with it — a
   *      panel-local flag would survive removal and reset on remount, both of
   *      which shipped as bugs before.
   *   2. It is the key the want-quantity budget is summed over, so three
   *      printings of one wanted card share one allowance.
   *
   * Absent on receive-side lines and on seeded lines (trade suggestions, the
   * have-list hover), which are deliberately unconstrained by a want budget.
   */
  originatingWantEntryId?: number;
  /**
   * Copies the recipient's want record asks for — the ceiling
   * `originatingWantEntryId` is measured against. Carried on the line so the
   * offer column can enforce it without re-reading the candidates response,
   * which it never fetches. Absent wherever `originatingWantEntryId` is.
   */
  wantEntryQuantity?: number;
  card: OfferCard;
}

/**
 * sessionStorage seed consumed by OfferBuilderContext. The have-list hover entry
 * point seeds one theySend item; a trade suggestion seeds both sides in full and
 * carries its suggestionId through to the POST body.
 */
export interface OfferBuilderSeed {
  counterparty: { id: number; username: string };
  youSend: OfferLineItem[];
  theySend: OfferLineItem[];
  suggestionId?: string;
}

export type OfferUpdateType =
  | 'offer_received'
  | 'offer_accepted'
  | 'offer_declined'
  | 'offer_expired'
  | 'offer_failed';

/** The single discriminated socket event; totals are from the receiving user's perspective */
export interface OfferUpdatePayload {
  offerId: string;
  type: OfferUpdateType;
  counterpartyUsername: string;
  theySendPoints: number;
  youSendPoints: number;
}

export interface OfferItemUnavailableDetails {
  cardName: string;
  setCode: string;
  setName: string;
  collectorNumber: string;
  condition: string;
  foil: boolean;
  language: string;
  requiredQuantity: number;
  availableQuantity: number;
}

export interface OfferInsufficientFundingDetails {
  yourShortfall: number;
  theirShortfall: number;
  expiresAt: string;
}

/**
 * OFFER_EXCEEDS_WANT_QUANTITY (400 at creation). The recipient's want record
 * asks for fewer copies than the offer charges to it, counting every line that
 * satisfies that record — three printings of one card are three lines against
 * one want.
 *
 * The server reports only the FIRST overflowing line: once one line is over,
 * the remaining budget is a fiction, because which card the user drops decides
 * what fits after it.
 *
 * `userMTGCardHaveId` is the reason this is worth a typed shape. The builder
 * keys its lines by have-row id, so the offending row can be marked exactly,
 * rather than string-matching a card name that appears on every printing.
 */
export interface OfferExceedsWantQuantityDetails {
  cardName: string;
  userMTGCardHaveId: number;
  offeredQuantity: number;
  remainingWantQuantity: number;
}

export const TRADE_OFFER_MAX_CARDS_PER_SIDE = 100;
